import CheckoutRate from './CheckoutRate';

/*
 * The checkout rate list is not simply what POST /shippingQuotes returns. The storefront checkout
 * (order2Controller_rest.js) composes three things into one list, and this module reproduces that so
 * SDK consumers get the same choices:
 *
 *   1. A flat-rate USPS option, for sample-request carts only.
 *   2. The real quoted carrier and site services.
 *   3. A "describe it yourself" option, on sites that enable a custom shipping method.
 *
 * The two synthesized options carry string service ids. The API takes them differently from a real
 * service: neither has a shippingServiceId, the custom one carries its description instead, and the
 * USPS one sets a flag. See buildShippingServicePatch in checkoutPatches.js.
 */

/** Sentinel service id for the shopper-described shipping option. */
export const CUSTOM_SHIPPING_SERVICE_ID = 'CUSTOM_SHIPPING_SERVICE';

/** Sentinel service id for the flat-rate USPS option. */
export const USPS_SERVICE_ID = 'USPS';

/**
 * The USPS option's name. This exact string is also what the API persists in the shipment's
 * shippingServiceNotes, so it doubles as the way a saved USPS selection is recognized.
 */
export const USPS_SERVICE_NAME = 'USPS Priority Mail';

/** The flat rate charged for the USPS sample-request option. Hardcoded in the storefront checkout too. */
export const USPS_FLAT_RATE = 8;

/**
 * The product that makes the USPS option available: system offering site share 29, which is the
 * "Sample Request" product on Smartpress. Samples are free, and the shopper pays flat-rate shipping,
 * so the option appears only when every item in the cart is a sample request. `SLS_` is the prefix
 * Collaterate uses for a system offering site share, so this compares directly against an order item's
 * productIdWithPrefix.
 */
export const SAMPLE_REQUEST_PRODUCT_ID_WITH_PREFIX = 'SLS_29';

/** The placeholder label shown for the shopper-described option before a description exists. */
export const CUSTOM_SERVICE_PROMPT = 'I will describe the shipping service';

/**
 * Whether the flat-rate USPS option should be offered for a project.
 *
 * @param {Project} project - The project being checked out.
 * @returns {boolean} True when the cart holds items and every one of them is a sample request.
 */

export function isUspsAvailable (project) {
    const items = (project && project.items) || [];

    return Boolean(items.length)
        && items.every(item => item.productIdWithPrefix === SAMPLE_REQUEST_PRODUCT_ID_WITH_PREFIX);
}

function buildUspsRate () {
    return new CheckoutRate({
        serviceId: USPS_SERVICE_ID,
        serviceName: USPS_SERVICE_NAME,
        price: USPS_FLAT_RATE,
        isUSPSService: true,
    });
}

/*
 * The custom option's label doubles as the saved description. It shows the generic prompt when there is
 * nothing shopper-written to show - that is, when a real service is already persisted, when the
 * persisted notes just echo a real service name, or when nothing is persisted at all. Otherwise the
 * previously typed description is shown so the shopper can see what they entered last time.
 */

function buildCustomRate (rates, shipment) {
    const persistedNotes = (shipment && shipment.shippingServiceNotes) || '';
    const persistedServiceId = shipment && shipment.shippingServiceId;
    const notesMatchRealService = rates.some(rate => rate.name === persistedNotes);
    const showPrompt = Boolean(persistedServiceId) || notesMatchRealService || !persistedNotes;

    return new CheckoutRate({
        serviceId: CUSTOM_SHIPPING_SERVICE_ID,
        serviceName: showPrompt ? CUSTOM_SERVICE_PROMPT : persistedNotes,
        price: null,
        isCustomService: true,
    });
}

/**
 * Compose the full list of shipping choices to offer a shopper.
 *
 * @param {Object} options - The inputs.
 * @param {Object} options.quoteResponse - The raw `POST /shippingQuotes` response.
 * @param {Project} options.project - The project being checked out.
 * @param {CheckoutSettings} options.settings - The storefront's checkout settings.
 * @param {?CheckoutShipment} options.shipment - The shipment as last resolved by the server.
 * @returns {CheckoutRate[]} The composed, frozen rate list.
 */

export function buildRateList ({ quoteResponse, project, settings, shipment }) {
    const quotes = (quoteResponse && quoteResponse.shippingServiceQuotes) || [];
    const rates = [];

    if (isUspsAvailable(project)) {
        rates.push(buildUspsRate());
    }

    quotes.forEach(quote => rates.push(new CheckoutRate(quote)));

    if (settings && settings.customShippingMethodEnabled) {
        rates.push(buildCustomRate(rates, shipment));
    }

    return Object.freeze(rates);
}

/**
 * Choose which rate should be selected for a checkout.
 *
 * Ported from order2Controller_rest.js initializeSelectedService(): reflect whatever the server already
 * persisted, and otherwise fall back to the cheapest carrier service so an order summary is never
 * blank. Neither synthesized option is ever auto-selected, and site-specific and unrated services are
 * not either - only a real carrier service is safe to pick on the shopper's behalf.
 *
 * @param {CheckoutRate[]} rates - The currently available rates.
 * @param {?CheckoutShipment} shipment - The shipment as last resolved by the server, if any.
 * @returns {?CheckoutRate} The rate to select, or null when there is nothing sensible to pick.
 */

export function findDefaultRate (rates, shipment) {
    const persistedServiceId = shipment && shipment.shippingServiceId;

    if (persistedServiceId) {
        const persisted = rates.find(rate => rate.serviceId === persistedServiceId);

        if (persisted) {
            return persisted;
        }
    }

    const persistedNotes = (shipment && shipment.shippingServiceNotes) || '';

    if (persistedNotes && !persistedServiceId) {
        // A saved USPS or shopper-described selection is identified by the notes, not by a service id.
        if (persistedNotes === USPS_SERVICE_NAME) {
            const usps = rates.find(rate => rate.isUSPSService);

            if (usps) {
                return usps;
            }
        }

        const custom = rates.find(rate => rate.isCustomService);

        if (custom) {
            return custom;
        }
    }

    const ratedVendorServices = rates.filter(rate => rate.isVendorService && rate.price !== null);

    if (!ratedVendorServices.length) {
        return null;
    }

    // Quotes arrive cheapest first today, but that is not a documented guarantee.
    return ratedVendorServices.reduce((cheapest, rate) => (rate.price < cheapest.price ? rate : cheapest));
}
